/**
 * Countries supported for shipping, derived from printful shipping rates.
 * Country codes are ISO 3166-1 alpha-2 (lowercase).
 */
export const SHIPPING_COUNTRIES = [
  "ad","ae","ag","ai","al","am","ao","ar","as","at","au","aw","az",
  "ba","bb","bd","be","bf","bg","bh","bi","bj","bm","bn","bo","br","bs","bw","bz",
  "ca","cd","cf","cg","ch","ci","ck","cl","cm","cn","co","cr","cv","cw","cy","cz",
  "de","dj","dk","dm","do","dz",
  "ee","eg","er","es","et",
  "fi","fj","fk","fm","fo","fr",
  "ga","gb","gd","ge","gf","gh","gi","gl","gm","gn","gp","gq","gr","gt","gu","gw","gy",
  "hk","hn","hr","ht","hu",
  "id","ie","il","im","in","iq","is","it",
  "je","jm","jo","jp",
  "ke","kg","kh","ki","km","kn","kr","kw","ky","kz",
  "lb","lc","li","lk","lr","ls","lt","lu","lv",
  "ma","mc","md","me","mf","mg","mh","mk","ml","mm","mo","mp","mq","mr","ms","mt","mu","mv","mw","mx","my","mz",
  "na","nc","ne","nf","ng","ni","nl","no","np","nr","nu","nz",
  "om",
  "pa","pe","pf","pg","ph","pk","pl","pm","pn","pr","ps","pt","pw","py",
  "qa",
  "re","ro","rs","rw",
  "sa","sb","sc","sd","se","sg","sh","si","sk","sl","sm","sn","so","sr","st","sv","sx","sz",
  "tc","tg","th","tj","tk","tn","to","tr","tt","tv","tw","tz",
  "ug","us","uy","uz",
  "va","vc","ve","vg","vi","vn","vu",
  "wf",
  "yt",
  "za","zm","zw",
] as const;

export type ShippingCountryCode = (typeof SHIPPING_COUNTRIES)[number];

/** Countries that require a state/province selection for accurate shipping rates. */
export const STATE_REQUIRED_COUNTRIES = new Set<ShippingCountryCode>(["us", "ca", "au", "br", "jp"]);

/** State/province codes per country that requires them. */
export const STATES: Record<string, { code: string; name: string }[]> = {
  us: [
    { code: "ak", name: "Alaska" },{ code: "al", name: "Alabama" },{ code: "ar", name: "Arkansas" },
    { code: "az", name: "Arizona" },{ code: "ca", name: "California" },{ code: "co", name: "Colorado" },
    { code: "ct", name: "Connecticut" },{ code: "dc", name: "District of Columbia" },{ code: "de", name: "Delaware" },
    { code: "fl", name: "Florida" },{ code: "ga", name: "Georgia" },{ code: "hi", name: "Hawaii" },
    { code: "ia", name: "Iowa" },{ code: "id", name: "Idaho" },{ code: "il", name: "Illinois" },
    { code: "in", name: "Indiana" },{ code: "ks", name: "Kansas" },{ code: "ky", name: "Kentucky" },
    { code: "la", name: "Louisiana" },{ code: "ma", name: "Massachusetts" },{ code: "md", name: "Maryland" },
    { code: "me", name: "Maine" },{ code: "mi", name: "Michigan" },{ code: "mn", name: "Minnesota" },
    { code: "mo", name: "Missouri" },{ code: "ms", name: "Mississippi" },{ code: "mt", name: "Montana" },
    { code: "nc", name: "North Carolina" },{ code: "nd", name: "North Dakota" },{ code: "ne", name: "Nebraska" },
    { code: "nh", name: "New Hampshire" },{ code: "nj", name: "New Jersey" },{ code: "nm", name: "New Mexico" },
    { code: "nv", name: "Nevada" },{ code: "ny", name: "New York" },{ code: "oh", name: "Ohio" },
    { code: "ok", name: "Oklahoma" },{ code: "or", name: "Oregon" },{ code: "pa", name: "Pennsylvania" },
    { code: "ri", name: "Rhode Island" },{ code: "sc", name: "South Carolina" },{ code: "sd", name: "South Dakota" },
    { code: "tn", name: "Tennessee" },{ code: "tx", name: "Texas" },{ code: "ut", name: "Utah" },
    { code: "va", name: "Virginia" },{ code: "vt", name: "Vermont" },{ code: "wa", name: "Washington" },
    { code: "wi", name: "Wisconsin" },{ code: "wv", name: "West Virginia" },{ code: "wy", name: "Wyoming" },
  ],
  ca: [
    { code: "ab", name: "Alberta" },{ code: "bc", name: "British Columbia" },{ code: "mb", name: "Manitoba" },
    { code: "nb", name: "New Brunswick" },{ code: "nl", name: "Newfoundland and Labrador" },{ code: "ns", name: "Nova Scotia" },
    { code: "nt", name: "Northwest Territories" },{ code: "nu", name: "Nunavut" },{ code: "on", name: "Ontario" },
    { code: "pe", name: "Prince Edward Island" },{ code: "qc", name: "Quebec" },{ code: "sk", name: "Saskatchewan" },
    { code: "yt", name: "Yukon" },
  ],
  au: [
    { code: "act", name: "Australian Capital Territory" },{ code: "nsw", name: "New South Wales" },
    { code: "nt", name: "Northern Territory" },{ code: "qld", name: "Queensland" },
    { code: "sa", name: "South Australia" },{ code: "tas", name: "Tasmania" },
    { code: "vic", name: "Victoria" },{ code: "wa", name: "Western Australia" },
  ],
  br: [
    { code: "ac", name: "Acre" },{ code: "al", name: "Alagoas" },{ code: "am", name: "Amazonas" },
    { code: "ap", name: "Amapá" },{ code: "ba", name: "Bahia" },{ code: "ce", name: "Ceará" },
    { code: "df", name: "Distrito Federal" },{ code: "es", name: "Espírito Santo" },{ code: "go", name: "Goiás" },
    { code: "ma", name: "Maranhão" },{ code: "mg", name: "Minas Gerais" },{ code: "ms", name: "Mato Grosso do Sul" },
    { code: "mt", name: "Mato Grosso" },{ code: "pa", name: "Pará" },{ code: "pb", name: "Paraíba" },
    { code: "pe", name: "Pernambuco" },{ code: "pi", name: "Piauí" },{ code: "pr", name: "Paraná" },
    { code: "rj", name: "Rio de Janeiro" },{ code: "rn", name: "Rio Grande do Norte" },{ code: "ro", name: "Rondônia" },
    { code: "rr", name: "Roraima" },{ code: "rs", name: "Rio Grande do Sul" },{ code: "sc", name: "Santa Catarina" },
    { code: "se", name: "Sergipe" },{ code: "sp", name: "São Paulo" },{ code: "to", name: "Tocantins" },
  ],
  jp: [
    { code: "01", name: "Hokkaido" },{ code: "02", name: "Aomori" },{ code: "03", name: "Iwate" },
    { code: "04", name: "Miyagi" },{ code: "05", name: "Akita" },{ code: "06", name: "Yamagata" },
    { code: "07", name: "Fukushima" },{ code: "08", name: "Ibaraki" },{ code: "09", name: "Tochigi" },
    { code: "10", name: "Gunma" },{ code: "11", name: "Saitama" },{ code: "12", name: "Chiba" },
    { code: "13", name: "Tokyo" },{ code: "14", name: "Kanagawa" },{ code: "15", name: "Niigata" },
    { code: "16", name: "Toyama" },{ code: "17", name: "Ishikawa" },{ code: "18", name: "Fukui" },
    { code: "19", name: "Yamanashi" },{ code: "20", name: "Nagano" },{ code: "21", name: "Gifu" },
    { code: "22", name: "Shizuoka" },{ code: "23", name: "Aichi" },{ code: "24", name: "Mie" },
    { code: "25", name: "Shiga" },{ code: "26", name: "Kyoto" },{ code: "27", name: "Osaka" },
    { code: "28", name: "Hyogo" },{ code: "29", name: "Nara" },{ code: "30", name: "Wakayama" },
    { code: "31", name: "Tottori" },{ code: "32", name: "Shimane" },{ code: "33", name: "Okayama" },
    { code: "34", name: "Hiroshima" },{ code: "35", name: "Yamaguchi" },{ code: "36", name: "Tokushima" },
    { code: "37", name: "Kagawa" },{ code: "38", name: "Ehime" },{ code: "39", name: "Kochi" },
    { code: "40", name: "Fukuoka" },{ code: "41", name: "Saga" },{ code: "42", name: "Nagasaki" },
    { code: "43", name: "Kumamoto" },{ code: "44", name: "Oita" },{ code: "45", name: "Miyazaki" },
    { code: "46", name: "Kagoshima" },{ code: "47", name: "Okinawa" },
  ],
};

/**
 * Build a sorted list of { code, name } for the country dropdown.
 * Uses Intl.DisplayNames if available, falls back to uppercase code.
 */
let _countryOptions: { code: string; name: string }[] | null = null;

export function getCountryOptions(): { code: string; name: string }[] {
  if (_countryOptions) return _countryOptions;
  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames(["en"], { type: "region" });
  } catch { /* browser doesn't support it */ }

  _countryOptions = SHIPPING_COUNTRIES.map((code) => {
    let name: string;
    try {
      name = displayNames?.of(code.toUpperCase()) ?? code.toUpperCase();
    } catch {
      name = code.toUpperCase();
    }
    return { code, name };
  }).sort((a, b) => a.name.localeCompare(b.name));

  return _countryOptions;
}
